import type { Company } from '../types';

/**
 * Fields that make up a company's "Praktisk information".
 * Kept as a Pick so the helper can be called with partial company data.
 */
export type PracticalInfoFields = Pick<
  Company,
  'companyPresentation' | 'geographicArea' | 'languages' | 'experienceYears'
>;

/**
 * A company's practical information is considered complete when all of the
 * following are filled in (threshold defined with the product owner):
 *  - companyPresentation (Företagspresentation) – non-empty text
 *  - geographicArea (Geografiskt område) – non-empty text
 *  - languages (Språk) – at least one language
 *  - experienceYears (Erfarenhetsår) – greater than 0
 */
export function isPracticalInfoComplete(
  company: PracticalInfoFields | null | undefined
): boolean {
  if (!company) return false;

  const hasPresentation =
    typeof company.companyPresentation === 'string' &&
    company.companyPresentation.trim().length > 0;
  const hasGeographicArea =
    typeof company.geographicArea === 'string' &&
    company.geographicArea.trim().length > 0;
  const hasLanguages =
    Array.isArray(company.languages) && company.languages.length > 0;
  const hasExperienceYears =
    typeof company.experienceYears === 'number' && company.experienceYears > 0;

  return hasPresentation && hasGeographicArea && hasLanguages && hasExperienceYears;
}
